/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import "./css/Advertisement.css";
import {
  getAllAds,
  getAdAnalytics,
  createAd,
  updateAd,
  toggleAdStatus,
  rejectAd,
  deleteAd,
  uploadAdMedia,
  deleteAdMedia,
} from "../api/advertisementApi";
import { toast } from "react-toastify";
import AccessControl from "../components/AccessControl";
import Loader from "../components/Loader";

// ════════════════════════════════════════════════════════════════════════════
// 🔧 HELPERS  (pure functions — no hooks, no state)
// ════════════════════════════════════════════════════════════════════════════
const STATUS_FILTERS = [
  "all",
  "active",
  "pending",
  "paused",
  "expired",
  "rejected",
];

// ════════════════════════════════════════════════════════════════════════════
// 💰 AD PRICING — image vs video flat rate (auto-filled into Amount field)
//    ⚠️ Keep these numbers in sync with AD_PRICING in advertisementController.js
// ════════════════════════════════════════════════════════════════════════════
const AD_PRICING = {
  image: 999,
  video: 2499,
};

// ════════════════════════════════════════════════════════════════════════════
// 📏 MEDIA SPECS — shown as guidance next to the upload box
// ════════════════════════════════════════════════════════════════════════════
const MEDIA_SPECS = {
  splash: { ratio: "9:16 (portrait)", reco: "1080 × 1920px" },
  banner: { ratio: "16:9 (landscape)", reco: "1280 × 720px" },
};

const statusBadge = (status) => {
  const map = {
    active: "badge badge-active",
    pending: "badge badge-pending",
    paused: "badge badge-paused",
    expired: "badge badge-expired",
    rejected: "badge badge-rejected",
  };
  const icons = {
    active: "✅",
    pending: "⏳",
    paused: "⏸️",
    expired: "💤",
    rejected: "❌",
  };
  return (
    <span className={map[status] || "badge badge-expired"}>
      {icons[status]} {status}
    </span>
  );
};

const payBadge = (status) => {
  const map = {
    paid: "badge pay-badge-paid",
    pending: "badge pay-badge-pending",
    failed: "badge pay-badge-failed",
  };
  return (
    <span className={map[status] || "badge pay-badge-pending"}>{status}</span>
  );
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

// ── Orientation validator (pure — no state access needed) ────────────────
const checkOrientation = (file, adType) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (adType === "splash") resolve(img.naturalHeight > img.naturalWidth);
      else resolve(img.naturalWidth > img.naturalHeight); // banner = horizontal
    };
    img.onerror = () => resolve(true); // can't read → allow upload
    img.src = URL.createObjectURL(file);
  });

// ════════════════════════════════════════════════════════════════════════════
// 📝 DEFAULT FORM STATE
// ════════════════════════════════════════════════════════════════════════════
const defaultForm = {
  advertiserName: "",
  advertiserPhone: "",
  advertiserEmail: "",
  businessName: "",
  title: "",
  description: "",
  mediaType: "image",
  adType: "splash", // "splash" | "banner"
  mediaUrl: "",
  publicId: "",
  thumbnailUrl: "",
  clickUrl: "",
  startDate: "",
  endDate: "",
  amount: "",
  paymentStatus: "pending",
  paymentNote: "",
  displayDurationSec: 5,
  targetAudience: "all",
  priority: 1,
  notes: "",
};

// ════════════════════════════════════════════════════════════════════════════
// 📢 ADVERTISEMENT PAGE
// ════════════════════════════════════════════════════════════════════════════
export default function AdvertisementPage() {
  // ── State ──────────────────────────────────────────────────────────────
  const [ads, setAds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editAd, setEditAd] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Form state
  const [form, setForm] = useState(defaultForm);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileRef = useRef(null);

  // Media full-view preview modal
  const [mediaPreview, setMediaPreview] = useState(null);
  // { url, mediaType, adType, title }

  // ── Data fetching ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchAds();
  }, [filter, page]);
  useEffect(() => {
    fetchAnalytics();
  }, []);

  async function fetchAds() {
    setLoading(true);
    const statusParam = filter === "all" ? "" : filter;
    const res = await getAllAds({ status: statusParam, page, limit: 15 });
    if (res.success) {
      setAds(res.ads || []);
      setTotalPages(res.pages || 1);
    } else {
      toast.error("Failed to load ads");
    }
    setLoading(false);
  }

  async function fetchAnalytics() {
    const res = await getAdAnalytics();
    if (res.success) setAnalytics(res.analytics);
  }

  // ── Form helpers ────────────────────────────────────────────────────────
  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditAd(null);
    setForm(defaultForm);
    setPreviewUrl("");
    setShowModal(true);
  };

  const openEdit = (ad) => {
    setEditAd(ad);
    setForm({
      advertiserName: ad.advertiserName || "",
      advertiserPhone: ad.advertiserPhone || "",
      advertiserEmail: ad.advertiserEmail || "",
      businessName: ad.businessName || "",
      title: ad.title || "",
      description: ad.description || "",
      mediaType: ad.mediaType || "image",
      adType: ad.adType || "splash",
      mediaUrl: ad.mediaUrl || "",
      publicId: ad.publicId || "",
      thumbnailUrl: ad.thumbnailUrl || "",
      clickUrl: ad.clickUrl || "",
      startDate: ad.startDate ? ad.startDate.slice(0, 10) : "",
      endDate: ad.endDate ? ad.endDate.slice(0, 10) : "",
      amount: ad.amount ?? "",
      paymentStatus: ad.paymentStatus || "pending",
      paymentNote: ad.paymentNote || "",
      displayDurationSec: ad.displayDurationSec || 5,
      targetAudience: ad.targetAudience || "all",
      priority: ad.priority || 1,
      notes: ad.notes || "",
    });
    setPreviewUrl(ad.mediaUrl || "");
    setShowModal(true);
  };

  // ── Media Upload ────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Orientation validation for images
    if (file.type.startsWith("image/")) {
      const isValidOrientation = await checkOrientation(file, form.adType);
      if (!isValidOrientation) {
        toast.error(
          form.adType === "splash"
            ? "Splash ad requires a VERTICAL image (height > width)"
            : "Banner ad requires a HORIZONTAL image (width > height)",
        );
        e.target.value = ""; // reset input
        return;
      }
    }

    setUploading(true);
    const res = await uploadAdMedia(file);
    setUploading(false);
    console.log(res, "<--- upload response");
    if (res.success) {
      setField("mediaUrl", res.mediaUrl);
      setField("publicId", res.publicId);
      setField("mediaType", res.mediaType);
      setPreviewUrl(res.mediaUrl);

      // ── Auto-fill Amount based on media type (image vs video rate) ──────
      // Only auto-fill when creating a fresh ad and the admin hasn't
      // already typed a custom amount, so we never clobber a manual edit.
      if (!editAd && (form.amount === "" || form.amount === 0)) {
        const suggested = res.suggestedAmount ?? AD_PRICING[res.mediaType] ?? 0;
        setField("amount", suggested);
        toast.info(
          `${res.mediaType === "video" ? "Video" : "Image"} ad rate ₹${suggested} auto-filled — editable below`,
        );
      } else {
        toast.success("Media uploaded!");
      }
    } else {
      toast.error("Upload failed: " + (res.message || res.error));
    }
  };

  // ── Delete Media  ← FIXED: moved INSIDE component so it can access state ──
  const handleDeleteMedia = () => {
    try {
      if (form.publicId) {
        console.log(form.publicId, "<--- publicId to delete");
        deleteAdMedia(form.publicId); // async call, but we won't await it since we want to update UI immediately
      }
    } catch (err) {
      console.error("Error deleting media:", err);
    }

    setField("mediaUrl", "");
    setField("publicId", "");
    setField("thumbnailUrl", "");
    setPreviewUrl("");
    if (fileRef.current) fileRef.current.value = "";
    toast.info("Media removed");
  };

  // ── Save (Create or Update) ─────────────────────────────────────────────
  const handleSave = async () => {
    if (
      !form.advertiserName ||
      !form.title ||
      !form.mediaUrl ||
      !form.startDate ||
      !form.endDate
    ) {
      toast.error("Please fill all required fields and upload media");
      return;
    }
    setLoading(true);
    const payload = { ...form, amount: Number(form.amount) || 0 };
    console.log(editAd, "<--- editAd");
    console.log(payload, "<--- payload to save");

    const res = editAd
      ? await updateAd(editAd._id, payload)
      : await createAd(payload);
    setLoading(false);

    if (res.success) {
      toast.success(
        editAd ? "Ad updated successfully!" : "Ad created successfully!",
      );
      setShowModal(false);
      fetchAds();
      fetchAnalytics();
    } else {
      toast.error("Error: " + (res.message || res.error));
    }
  };

  // ── Toggle Active / Paused ──────────────────────────────────────────────
  const handleToggle = async (ad) => {
    setLoading(true);
    const res = await toggleAdStatus(ad.adId);
    setLoading(false);
    if (res.success) {
      toast.success(`Ad ${res.status}`);
      fetchAds();
      fetchAnalytics();
    } else {
      toast.error(res.message || "Toggle failed");
    }
  };

  // ── Reject ──────────────────────────────────────────────────────────────
  const openReject = (ad) => {
    setRejectTarget(ad);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Enter rejection reason");
      return;
    }
    setLoading(true);
    const res = await rejectAd(rejectTarget.adId, rejectReason);
    setLoading(false);
    if (res.success) {
      toast.success("Ad rejected");
      setShowRejectModal(false);
      fetchAds();
      fetchAnalytics();
    } else {
      toast.error(res.message || "Reject failed");
    }
  };

  // ── Delete Ad ───────────────────────────────────────────────────────────
  const handleDelete = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.title}"? This cannot be undone.`))
      return;
    setLoading(true);
    const res = await deleteAd(ad.adId);
    setLoading(false);
    if (res.success) {
      toast.success("Ad deleted");
      fetchAds();
      fetchAnalytics();
    } else {
      toast.error(res.message || "Delete failed");
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // 🖼️ RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="advertisement-page">
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />

      {loading && <Loader />}

      {/* ─── ANALYTICS STATS ROW ─────────────────────────────────────── */}
      {analytics && (
        <div className="stats-row">
          <div className="stat-card">
            <span className="stat-label">Total Ads</span>
            <span className="stat-value blue">{analytics.total}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Active</span>
            <span className="stat-value green">{analytics.active}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Pending</span>
            <span className="stat-value yellow">{analytics.pending}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Expired</span>
            <span className="stat-value">{analytics.expired}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Rejected</span>
            <span className="stat-value red">{analytics.rejected}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Revenue</span>
            <span className="stat-value green">
              ₹{analytics.totalRevenue?.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      )}

      {/* ─── ADS TABLE CARD ──────────────────────────────────────────── */}
      <div className="card section">
        <div className="card-header">
          <span className="card-title">Advertisements</span>
          <div className="toolbar">
            {/* Filter tabs */}
            <div className="filter-tabs">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  className={`filter-tab ${filter === s ? "active" : ""}`}
                  onClick={() => {
                    setFilter(s);
                    setPage(1);
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            {/* Add button */}
            <button className="btn-add" onClick={openCreate}>
              ➕ New Ad
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="ad-table-wrap">
          <table className="ad-table">
            <thead>
              <tr>
                <th>Media</th>
                <th>Ad Details</th>
                <th>Schedule</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Stats</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ads.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-icon">📢</div>
                      <p>No advertisements found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                ads.map((ad) => (
                  <tr key={ad._id}>
                    {/* Media Thumbnail — click to full preview */}
                    <td>
                      {ad.mediaUrl ? (
                        ad.mediaType === "video" ? (
                          <div
                            className="ad-thumb-placeholder"
                            style={{ cursor: "pointer" }}
                            title="Click to preview"
                            onClick={() =>
                              setMediaPreview({
                                url: ad.mediaUrl,
                                mediaType: ad.mediaType,
                                adType: ad.adType || "splash",
                                title: ad.title,
                              })
                            }
                          >
                            🎬
                          </div>
                        ) : (
                          <img
                            src={ad.thumbnailUrl || ad.mediaUrl}
                            alt={ad.title}
                            className="ad-thumb"
                            style={{ cursor: "pointer" }}
                            title="Click to preview"
                            onClick={() =>
                              setMediaPreview({
                                url: ad.mediaUrl,
                                mediaType: ad.mediaType,
                                adType: ad.adType || "splash",
                                title: ad.title,
                              })
                            }
                          />
                        )
                      ) : (
                        <div className="ad-thumb-placeholder">🖼️</div>
                      )}
                    </td>

                    {/* Title + Advertiser */}
                    <td>
                      <div className="ad-title-cell">
                        <div className="ad-title-text">{ad.title}</div>
                        <div className="ad-advertiser">
                          {ad.advertiserName}
                          {ad.businessName ? ` · ${ad.businessName}` : ""}
                        </div>
                        <div
                          className="ad-advertiser"
                          style={{ color: "#9ca3af" }}
                        >
                          {ad.adId}
                        </div>
                        {/* Ad type badge */}
                        {ad.adType && (
                          <div style={{ marginTop: 3 }}>
                            <span
                              style={{
                                fontSize: 10,
                                padding: "2px 7px",
                                borderRadius: 4,
                                background:
                                  ad.adType === "splash"
                                    ? "#eff6ff"
                                    : "#f0fdf4",
                                border: `1px solid ${ad.adType === "splash" ? "#bfdbfe" : "#bbf7d0"}`,
                                color:
                                  ad.adType === "splash"
                                    ? "#1d4ed8"
                                    : "#15803d",
                                fontWeight: 600,
                              }}
                            >
                              {ad.adType === "splash"
                                ? "🖼️ Splash"
                                : "📰 Banner"}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Schedule */}
                    <td>
                      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                        <div>📅 {fmtDate(ad.startDate)}</div>
                        <div>🏁 {fmtDate(ad.endDate)}</div>
                        <div style={{ color: "#9ca3af" }}>
                          ⏱ {ad.displayDurationSec}s
                        </div>
                      </div>
                    </td>

                    {/* Amount */}
                    <td>
                      <strong>
                        ₹{Number(ad.amount || 0).toLocaleString("en-IN")}
                      </strong>
                    </td>

                    {/* Payment */}
                    <td>{payBadge(ad.paymentStatus)}</td>

                    {/* Status */}
                    <td>{statusBadge(ad.status)}</td>

                    {/* Stats */}
                    <td>
                      <div
                        style={{
                          fontSize: 12,
                          lineHeight: 1.8,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div>👁 {ad.impressions || 0} views</div>
                        <div>👆 {ad.clicks || 0} clicks</div>
                        {ad.impressions > 0 && (
                          <div style={{ color: "#9ca3af" }}>
                            CTR:{" "}
                            {((ad.clicks / ad.impressions) * 100).toFixed(1)}%
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="action-btns">
                        <button
                          className="btn-icon"
                          title="Edit"
                          onClick={() => openEdit(ad)}
                        >
                          ✏️
                        </button>

                        {["active", "paused", "pending"].includes(
                          ad.status,
                        ) && (
                          <button
                            className="btn-icon toggle-pause"
                            title={
                              ad.status === "active" ? "Pause" : "Activate"
                            }
                            onClick={() => handleToggle(ad)}
                          >
                            {ad.status === "active" ? "⏸️" : "▶️"}
                          </button>
                        )}

                        {["pending", "active"].includes(ad.status) && (
                          <button
                            className="btn-icon btn-reject"
                            title="Reject"
                            onClick={() => openReject(ad)}
                          >
                            🚫
                          </button>
                        )}

                        <button
                          className="btn-icon btn-delete"
                          title="Delete"
                          onClick={() => handleDelete(ad)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="page-btn"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                className={`page-btn ${p === page ? "active" : ""}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              className="page-btn"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              ›
            </button>
            <span className="page-info">
              Page {page} of {totalPages}
            </span>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          📝 CREATE / EDIT MODAL
      ════════════════════════════════════════════════════════════════════ */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">
                {editAd ? "Edit Advertisement" : "New Advertisement"}
              </span>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* ── Advertiser Info ── */}
              <div className="form-section-title">📋 Advertiser Info</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Advertiser Name *</label>
                  <input
                    className="form-input"
                    placeholder="John Doe"
                    value={form.advertiserName}
                    onChange={(e) => setField("advertiserName", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Business Name</label>
                  <input
                    className="form-input"
                    placeholder="ABC Stores"
                    value={form.businessName}
                    onChange={(e) => setField("businessName", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    className="form-input"
                    placeholder="9876543210"
                    value={form.advertiserPhone}
                    onChange={(e) =>
                      setField("advertiserPhone", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="abc@mail.com"
                    value={form.advertiserEmail}
                    onChange={(e) =>
                      setField("advertiserEmail", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* ── Ad Content ── */}
              <div className="form-section-title">🖼️ Ad Content</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Ad Title *</label>
                  <input
                    className="form-input"
                    placeholder="Big Sale This Weekend!"
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Click URL</label>
                  <input
                    className="form-input"
                    placeholder="https://..."
                    value={form.clickUrl}
                    onChange={(e) => setField("clickUrl", e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  placeholder="Brief ad description..."
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </div>

              {/* ── Ad Type ── */}
              <div className="form-section-title">📐 Ad Type</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Ad Type *</label>
                  <select
                    className="form-select"
                    value={form.adType}
                    onChange={(e) => {
                      setField("adType", e.target.value);
                      // Clear media when type changes — orientation will differ
                      setField("mediaUrl", "");
                      setField("thumbnailUrl", "");
                      setPreviewUrl("");
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                  >
                    <option value="splash">🖼️ Splash Screen (Vertical)</option>
                    <option value="banner">📰 Banner (Horizontal)</option>
                  </select>
                </div>
                <div
                  className="form-group"
                  style={{ display: "flex", alignItems: "flex-end" }}
                >
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      background:
                        form.adType === "splash" ? "#eff6ff" : "#f0fdf4",
                      border: `1px solid ${form.adType === "splash" ? "#bfdbfe" : "#bbf7d0"}`,
                      fontSize: 12,
                      color: "#374151",
                      width: "100%",
                    }}
                  >
                    {form.adType === "splash"
                      ? "📱 Full-screen vertical ad — shown between screens. Image must be taller than wide."
                      : "📰 Horizontal strip ad — shown at top/bottom. Image must be wider than tall."}
                  </div>
                </div>
              </div>

              {/* ── Media Upload ── */}
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">
                  Ad Media *&nbsp;
                  <span
                    style={{ fontWeight: 400, color: "#6b7280", fontSize: 11 }}
                  >
                    {form.adType === "splash"
                      ? "(Vertical image/video — portrait 9:16)"
                      : "(Horizontal image/video — landscape 16:9)"}
                  </span>
                </label>

                {/* ── Preview container — shape changes per adType ── */}
                {previewUrl ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 10,
                      padding: 12,
                      border: "2px dashed #d1d5db",
                      borderRadius: 10,
                      background: "#f9fafb",
                    }}
                  >
                    {form.mediaType === "video" ? (
                      /* ── Video preview ── */
                      <video
                        src={previewUrl}
                        controls
                        style={{
                          width: form.adType === "splash" ? 160 : "100%",
                          height: form.adType === "splash" ? 284 : "auto",
                          maxHeight: form.adType === "splash" ? 284 : 220,
                          objectFit: "cover",
                          borderRadius: 8,
                          background: "#000",
                          display: "block",
                        }}
                      />
                    ) : (
                      /* ── Image preview ── */
                      <img
                        src={previewUrl}
                        alt="Ad preview"
                        style={{
                          /* Splash → portrait box (9:16),  Banner → landscape box (16:9) */
                          width: form.adType === "splash" ? 160 : "100%",
                          height: form.adType === "splash" ? 284 : "auto",
                          maxHeight: form.adType === "splash" ? 284 : 220,
                          objectFit: "cover",
                          borderRadius: 8,
                          display: "block",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    )}

                    {/* Type label under preview */}
                    <span
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      {form.adType === "splash"
                        ? "📱 Splash — Portrait preview (9:16)"
                        : "📰 Banner — Landscape preview (16:9)"}
                    </span>

                    {/* Change / Remove buttons */}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        style={{
                          padding: "5px 14px",
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 6,
                          color: "#1d4ed8",
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        🔄 Change Media
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteMedia}
                        style={{
                          padding: "5px 14px",
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          borderRadius: 6,
                          color: "#dc2626",
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        🗑️ Remove Media
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Upload placeholder ── */
                  <div
                    className="upload-area"
                    onClick={() => fileRef.current?.click()}
                    style={{ cursor: "pointer" }}
                  >
                    <p>
                      📁 Click to upload{" "}
                      {form.adType === "splash"
                        ? "vertical (portrait)"
                        : "horizontal (landscape)"}{" "}
                      image or video
                    </p>
                    <p className="upload-hint">
                      Recommended: {MEDIA_SPECS[form.adType].reco} (
                      {MEDIA_SPECS[form.adType].ratio})
                      <br />
                      🖼️ Image — JPG / PNG / WebP · Max 5MB · ₹
                      {AD_PRICING.image}
                      <br />
                      🎬 Video — MP4 / MOV · Max 50MB · ₹{AD_PRICING.video}
                    </p>
                    {uploading && (
                      <p className="upload-progress">⬆️ Uploading...</p>
                    )}
                  </div>
                )}

                {/* uploading spinner shown even when previewUrl exists (re-upload) */}
                {uploading && previewUrl && (
                  <p style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                    ⬆️ Uploading new media...
                  </p>
                )}

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              {/* ── Schedule ── */}
              <div className="form-section-title">📅 Schedule</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Start Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.startDate}
                    onChange={(e) => setField("startDate", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.endDate}
                    onChange={(e) => setField("endDate", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Display Duration (sec)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-input"
                      min={3}
                      max={30}
                      value={form.displayDurationSec}
                      onChange={(e) =>
                        setField("displayDurationSec", Number(e.target.value))
                      }
                    />
                    <span className="input-suffix">sec</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <input
                    type="number"
                    className="form-input"
                    min={1}
                    max={10}
                    value={form.priority}
                    onChange={(e) =>
                      setField("priority", Number(e.target.value))
                    }
                  />
                </div>
              </div>

              {/* ── Billing ── */}
              <div className="form-section-title">💳 Billing</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Amount&nbsp;
                    <span
                      style={{
                        fontWeight: 400,
                        color: "#9ca3af",
                        fontSize: 11,
                      }}
                    >
                      (auto-filled by media type · editable)
                    </span>
                  </label>
                  <div className="input-group">
                    <span className="input-prefix">₹</span>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={form.amount}
                      onChange={(e) => setField("amount", e.target.value)}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    Standard rate — Image ₹{AD_PRICING.image} · Video ₹
                    {AD_PRICING.video}
                  </span>
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <select
                    className="form-select"
                    value={form.paymentStatus}
                    onChange={(e) => setField("paymentStatus", e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Payment Note</label>
                <input
                  className="form-input"
                  placeholder="UPI ref, bank transfer etc."
                  value={form.paymentNote}
                  onChange={(e) => setField("paymentNote", e.target.value)}
                />
              </div>

              {/* ── Display Config ── */}
              <div className="form-section-title">⚙️ Display Config</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Target Audience</label>
                  <select
                    className="form-select"
                    value={form.targetAudience}
                    onChange={(e) => setField("targetAudience", e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="local">Local</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Admin Notes</label>
                  <input
                    className="form-input"
                    placeholder="Internal notes..."
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                </div>
              </div>
            </div>
            {/* end modal-body */}

            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleSave}
                disabled={loading || uploading}
              >
                {loading ? "Saving..." : editAd ? "Update Ad" : "Create Ad"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          ❌ REJECT MODAL
      ════════════════════════════════════════════════════════════════════ */}
      {showRejectModal && (
        <div
          className="modal-overlay"
          onClick={(e) =>
            e.target === e.currentTarget && setShowRejectModal(false)
          }
        >
          <div className="modal reject-modal">
            <div className="modal-header">
              <span className="modal-title">🚫 Reject Ad</span>
              <button
                className="modal-close"
                onClick={() => setShowRejectModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: "#374151", marginBottom: 14 }}>
                Rejecting: <strong>{rejectTarget?.title}</strong>
              </p>
              <div className="form-group">
                <label className="form-label">Rejection Reason *</label>
                <textarea
                  className="form-textarea"
                  placeholder="Enter reason for rejection..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowRejectModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleReject}
                disabled={loading}
              >
                {loading ? "Rejecting..." : "Reject Ad"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          🖼️ MEDIA FULL-VIEW PREVIEW MODAL
      ════════════════════════════════════════════════════════════════════ */}
      {mediaPreview && (
        <div
          onClick={() => setMediaPreview(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            cursor: "zoom-out",
          }}
        >
          {/* Header */}
          <div
            style={{
              width: "100%",
              maxWidth: mediaPreview.adType === "splash" ? 400 : 900,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 700,
                  background:
                    mediaPreview.adType === "splash" ? "#1d4ed8" : "#15803d",
                  color: "#fff",
                }}
              >
                {mediaPreview.adType === "splash"
                  ? "🖼️ Splash — Portrait"
                  : "📰 Banner — Landscape"}
              </span>
              <span style={{ color: "#e5e7eb", fontSize: 14, fontWeight: 600 }}>
                {mediaPreview.title}
              </span>
            </div>
            <button
              onClick={() => setMediaPreview(null)}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                borderRadius: "50%",
                width: 36,
                height: 36,
                fontSize: 20,
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>

          {/* Media */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: mediaPreview.adType === "splash" ? "auto" : "100%",
              maxWidth: mediaPreview.adType === "splash" ? 380 : 900,
              maxHeight: "80vh",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
              background: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {mediaPreview.mediaType === "video" ? (
              <video
                src={mediaPreview.url}
                controls
                autoPlay
                style={{
                  width: mediaPreview.adType === "splash" ? 380 : "100%",
                  height: mediaPreview.adType === "splash" ? "80vh" : "auto",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <img
                src={mediaPreview.url}
                alt={mediaPreview.title}
                style={{
                  width: mediaPreview.adType === "splash" ? 380 : "100%",
                  height: mediaPreview.adType === "splash" ? "80vh" : "auto",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            )}
          </div>

          {/* Hint */}
          <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 14 }}>
            Click anywhere outside to close
          </p>
        </div>
      )}
    </div>
  );
}
